/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { getMonitorAgents } from "@/api/monitor";
import { getSNMPDevices } from "@/api/snmp";
import { getDockerHosts } from "@/api/docker";
import { getRouters } from "@/api/routers";
import { getServices } from "@/api/services";
import { requestFocus } from "@/utils/sidebarFocus";
import { cn } from "@/lib/utils";

interface NavTab {
  id: string;
  label: string;
  icon?: React.ReactNode;
}

interface Command {
  id: string;
  label: string;
  group: string;
  hint?: string;
  icon?: React.ReactNode;
  run: () => void;
}

interface Props {
  tabs: NavTab[];
  onTabChange: (tabId: string) => void;
}

/**
 * Global command palette (Ctrl/Cmd+K): fuzzy search over sections, devices and
 * services, then jump + select via the focus bus. Data comes from the shared
 * react-query cache, so opening it triggers no extra requests.
 */
export const CommandPalette: React.FC<Props> = ({ tabs, onTabChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { data: agents = [] } = useQuery({ queryKey: ["monitor-agents"], queryFn: getMonitorAgents, enabled: open, staleTime: 30_000 });
  const { data: switches = [] } = useQuery({ queryKey: ["snmp-devices"], queryFn: getSNMPDevices, enabled: open, staleTime: 30_000 });
  const { data: dockerHosts = [] } = useQuery({ queryKey: ["docker-hosts"], queryFn: getDockerHosts, enabled: open, staleTime: 30_000 });
  const { data: routers = [] } = useQuery({ queryKey: ["routers"], queryFn: getRouters, enabled: open, staleTime: 30_000 });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: getServices, enabled: open, staleTime: 30_000 });

  // Global Ctrl/Cmd+K toggle.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("nyrvex:palette", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("nyrvex:palette", onOpen);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const go = (tabId: string, scope?: string, id?: string) => {
    onTabChange(tabId);
    if (scope && id) requestFocus(scope, id);
    setOpen(false);
  };

  const commands = useMemo<Command[]>(() => {
    const cmds: Command[] = [];
    for (const t of tabs) cmds.push({ id: `go-${t.id}`, label: t.label, group: "Go to", icon: t.icon, run: () => go(t.id) });
    for (const a of agents) cmds.push({ id: `srv-${a.id}`, label: a.name, group: "Server", hint: a.url, run: () => go("monitor", "monitor", String(a.id)) });
    for (const d of switches) cmds.push({ id: `sw-${d.id}`, label: d.name, group: "Switch", hint: d.host, run: () => go("switches", "switches", String(d.id)) });
    for (const h of dockerHosts) cmds.push({ id: `dk-${h.id}`, label: h.name, group: "Docker", hint: h.host, run: () => go("docker", "docker", String(h.id)) });
    for (const r of routers) cmds.push({ id: `rt-${r.id}`, label: r.name, group: "Router", run: () => go("routers", "routers", r.id) });
    for (const s of services) cmds.push({ id: `svc-${s.id}`, label: s.name, group: "Service", hint: s.url, run: () => { window.open(s.url, "_blank", "noopener"); setOpen(false); } });
    return cmds;
  }, [tabs, agents, switches, dockerHosts, routers, services]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? commands.filter((c) => `${c.label} ${c.group} ${c.hint ?? ""}`.toLowerCase().includes(q))
      : commands;
    return list.slice(0, 50);
  }, [commands, query]);

  useEffect(() => {
    if (active >= filtered.length) setActive(0);
  }, [filtered.length, active]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { setOpen(false); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); filtered[active]?.run(); }
  };

  // Keep the active row in view.
  const setRow = (el: HTMLButtonElement | null, i: number) => {
    if (el && i === active) el.scrollIntoView({ block: "nearest" });
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[12vh]"
      onMouseDown={() => setOpen(false)}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-xl overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="flex items-center gap-2.5 border-b border-gray-200 px-4 dark:border-gray-800">
          <MagnifyingGlassIcon className="h-4 w-4 flex-none text-gray-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }}
            placeholder="Search devices, services, sections…"
            className="h-12 w-full bg-transparent text-[14px] text-gray-900 outline-none placeholder:text-gray-400 dark:text-gray-100"
          />
          <kbd className="flex-none rounded-[3px] border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-400 dark:border-gray-700">ESC</kbd>
        </div>

        <div ref={listRef} className="modal-scrollbar max-h-[55vh] overflow-y-auto py-1.5">
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">No results</p>
          ) : (
            filtered.map((c, i) => (
              <button
                key={c.id}
                ref={(el) => setRow(el, i)}
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => c.run()}
                className={cn(
                  "flex w-full items-center gap-3 px-4 py-2 text-left",
                  i === active ? "bg-blue-500/10" : "",
                )}
              >
                {c.icon && (
                  <span className="flex h-4 w-4 flex-none items-center justify-center text-gray-400 [&_svg]:h-4 [&_svg]:w-4">
                    {c.icon}
                  </span>
                )}
                <span className="flex-none w-[70px] text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">{c.group}</span>
                <span className="flex-1 truncate text-[13.5px] text-gray-900 dark:text-gray-100">{c.label}</span>
                {c.hint && <span className="flex-none truncate max-w-[40%] text-[11px] text-gray-400 dark:text-gray-500">{c.hint}</span>}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
